import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { categories, subCategoryMap, getCategoryByType } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/feature/Pagination";
import { Link } from "react-router-dom";

const PAGE_SIZE = 6;

const timeRanges = [
  { key: "week", label: "最近一周" },
  { key: "month", label: "最近一个月" },
  { key: "all", label: "全部" },
];

function withinRange(dateStr: string, range: string) {
  if (range === "all") return true;
  const diff = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24);
  if (range === "week") return diff <= 7;
  if (range === "month") return diff <= 31;
  return true;
}

export default function CategoryPage() {
  const { type = "news" } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const isSearch = type === "search";

  const [range, setRange] = useState("all");
  const [sub, setSub] = useState("all");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);

  const category = getCategoryByType(type);

  useEffect(() => {
    setRange("all");
    setSub("all");
    setPage(1);
  }, [type, query]);

  const filteredNews = useMemo(() => {
    let list = [...newsList];

    if (isSearch) {
      const kw = query.toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(kw) ||
          n.summary.toLowerCase().includes(kw) ||
          n.tags.some((t) => t.toLowerCase().includes(kw))
      );
    } else {
      list = list.filter((n) => n.category === type);
      if (range !== "all") list = list.filter((n) => withinRange(n.date, range));
      if (sub !== "all") list = list.filter((n) => n.subCategory === sub);
    }

    list.sort((a, b) =>
      sort === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    );
    return list;
  }, [type, isSearch, query, range, sub, sort]);

  const matchedWorks = useMemo(() => {
    if (!isSearch) return [];
    const kw = query.toLowerCase();
    return worksList.filter(
      (w) =>
        w.title.toLowerCase().includes(kw) ||
        w.tags.some((t) => t.toLowerCase().includes(kw)) ||
        w.synopsis.toLowerCase().includes(kw)
    );
  }, [isSearch, query]);

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE));
  const pagedNews = filteredNews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [range, sub, sort]);

  const pageTitle = isSearch
    ? `${query || "搜索"} - 小泽玛利亚资讯搜索`
    : `小泽玛利亚 · ${category?.name ?? "资讯"} - 明星最新资讯`;
  const pageDesc = isSearch
    ? `在站内搜索“${query}”相关的资讯与影视作品。`
    : category?.description ?? "小泽玛利亚最新资讯分类页面。";

  useSEO({
    title: pageTitle,
    description: pageDesc,
    keywords: `小泽玛利亚,${category?.name ?? "资讯"},明星资讯,最新动态`,
  });

  if (!isSearch && !category) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-6 py-20 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-400"></i>
        <h1 className="font-heading text-2xl font-semibold text-foreground-950 mt-4">分类不存在</h1>
        <p className="text-sm text-foreground-600 mt-2">你访问的分类暂时没有内容。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const subs = category ? subCategoryMap[category.type] ?? [] : [];

  return (
    <div className="animate-fade-up mx-auto max-w-content px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={
          isSearch
            ? [{ label: "搜索" }]
            : [{ label: "资讯" }, { label: category?.name ?? "" }]
        }
      />

      <div className="mt-5 mb-6 md:mb-8">
        <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
          {isSearch ? "Search Result" : "Category"}
        </span>
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mt-1.5">
          {isSearch ? `“${query}” 的搜索结果` : category?.name}
        </h1>
        <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
          {isSearch
            ? `共找到 ${filteredNews.length} 条相关资讯${matchedWorks.length ? ` 与 ${matchedWorks.length} 部相关作品` : ""}。`
            : category?.description}
        </p>
      </div>

      {isSearch && matchedWorks.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-film-line text-primary-600"></i>
            相关影视作品
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {matchedWorks.slice(0, 4).map((w) => (
              <WorkCard key={w.id} item={w} />
            ))}
          </div>
        </section>
      )}

      {!isSearch && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 mb-6 md:mb-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm text-foreground-500 w-16 shrink-0">时间范围</span>
            <div className="flex flex-wrap gap-2">
              {timeRanges.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRange(r.key)}
                  className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    range === r.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {subs.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm text-foreground-500 w-16 shrink-0">资讯类型</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSub("all")}
                  className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    sub === "all"
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  全部
                </button>
                {subs.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSub(s)}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      sub === s
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm text-foreground-500 w-16 shrink-0">排序</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSort("desc")}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  sort === "desc"
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-accent-100"
                }`}
              >
                最新优先
              </button>
              <button
                type="button"
                onClick={() => setSort("asc")}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  sort === "asc"
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-accent-100"
                }`}
              >
                最早优先
              </button>
            </div>
          </div>
        </div>
      )}

      {pagedNews.length === 0 ? (
        <div className="py-16 text-center">
          <i className="ri-inbox-line text-5xl text-foreground-300"></i>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mt-4">暂无相关资讯</h2>
          <p className="text-sm text-foreground-500 mt-2">换个筛选条件，或者先回首页看看其他内容吧。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {pagedNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {!isSearch && (
        <div className="mt-12 pt-8 border-t border-background-200">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">浏览其他分类</h3>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter((c) => c.type !== type)
              .map((c) => (
                <Link
                  key={c.type}
                  to={`/category/${c.type}`}
                  className="px-3.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}